import React, { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, Loader2, Clock } from "lucide-react";
import { adminApi } from "../../lib/api";
import { PageHeader, SectionHeader, Card } from "./adminUi";

/**
 * System Health — safe, read-only integration status board.
 * Never displays: API keys, webhook secrets, JWT/SSO/ingest secrets, or any
 * env var VALUES. The backend `/admin/system-health` endpoint only emits
 * status booleans + tiny derived notes; this component just formats them.
 */
const LABELS = {
  lifeguide: "LifeGuide backend",
  database: "Database",
  i_need_a_proverb: "I Need a Proverb",
  proverb_analytics_connection: "Proverb Analytics Connection",
  stripe: "Stripe",
  resend: "Resend",
  twilio: "Twilio — A2P Approved",
};

const ORDER = [
  "lifeguide",
  "database",
  "i_need_a_proverb",
  "proverb_analytics_connection",
  "stripe",
  "resend",
  "twilio",
];

export default function AdminSystemHealthPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    adminApi
      .systemHealth()
      .then(setData)
      .catch((e) => setErr(e?.response?.data?.detail || "Unable to load"));
  }, []);

  return (
    <div data-testid="admin-system-health-page" className="space-y-8">
      <PageHeader
        eyebrow="Operations"
        title="System Health"
        subtitle="Integration status only — never exposes API keys, webhook secrets, or environment variable values."
      />

      {err && (
        <div className="rounded-2xl border border-[#B4795C]/50 bg-[#241914] text-[#F3D6A6] p-4 text-sm">
          {err}
        </div>
      )}

      <section>
        <SectionHeader label="Services" />
        {!data ? (
          <div className="text-[#8FA894] text-sm">Checking…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ORDER.map((key) => {
              const s = data.services?.[key];
              if (!s) return null;
              return <StatusCard key={key} name={LABELS[key]} status={s.status} note={s.note} testid={`health-${key}`} />;
            })}
          </div>
        )}
      </section>

      <section>
        <SectionHeader label="Privacy" />
        <Card className="text-sm text-[#B7D2AF] leading-relaxed">
          This page renders status labels only. Secrets such as{" "}
          <code>STRIPE_API_KEY</code>, <code>RESEND_API_KEY</code>,{" "}
          <code>TWILIO_AUTH_TOKEN</code>, <code>PROVERB_INGEST_SECRET</code>,
          and JWT signing keys are checked by the backend for presence and are
          never returned to any client.
        </Card>
      </section>

      {data?.checked_at && (
        <div className="text-xs text-[#7C8F7C]" data-testid="health-checked-at">
          Checked {data.checked_at.slice(0, 19).replace("T", " ")} UTC
        </div>
      )}
    </div>
  );
}

function StatusCard({ name, status, note, testid }) {
  const meta = statusMeta(status);
  const Icon = meta.icon;
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border ${meta.border} bg-[#141B18] p-4 flex items-start gap-3`}
    >
      <Icon size={18} className={`${meta.iconCls} mt-0.5`} />
      <div className="min-w-0">
        <div className="text-white font-semibold text-sm">{name}</div>
        <div className={`text-[11px] uppercase tracking-[0.14em] mt-0.5 ${meta.textCls}`}>
          {meta.label}
        </div>
        {note && <div className="text-xs text-[#8FA894] mt-1.5 leading-relaxed">{note}</div>}
      </div>
    </div>
  );
}

function statusMeta(status) {
  switch (status) {
    case "ok":
      return { icon: CheckCircle2, iconCls: "text-[#8DBF88]", label: "Operational", border: "border-[#4F6F52]/40", textCls: "text-[#8DBF88]" };
    case "approved":
      return { icon: CheckCircle2, iconCls: "text-[#8DBF88]", label: "Approved — outbound disabled", border: "border-[#4F6F52]/40", textCls: "text-[#8DBF88]" };
    case "pending_a2p_review":
      return { icon: Clock, iconCls: "text-[#F3D6A6]", label: "Pending A2P review", border: "border-[#B4795C]/40", textCls: "text-[#F3D6A6]" };
    case "configured_no_events":
      return { icon: Loader2, iconCls: "text-[#F3D6A6]", label: "Configured — no events yet", border: "border-[#B4795C]/40", textCls: "text-[#F3D6A6]" };
    case "awaiting_first_event":
      return { icon: Loader2, iconCls: "text-[#F3D6A6]", label: "Awaiting first event", border: "border-[#B4795C]/40", textCls: "text-[#F3D6A6]" };
    case "not_configured":
      return { icon: AlertTriangle, iconCls: "text-[#F3D6A6]", label: "Not configured", border: "border-[#B4795C]/40", textCls: "text-[#F3D6A6]" };
    case "degraded":
    case "down":
      return { icon: AlertTriangle, iconCls: "text-[#E38680]", label: status === "down" ? "Down" : "Degraded", border: "border-[#7C3E33]/50", textCls: "text-[#E38680]" };
    default:
      return { icon: AlertTriangle, iconCls: "text-[#B7D2AF]", label: status || "Unknown", border: "border-white/10", textCls: "text-[#B7D2AF]" };
  }
}
